import { Suspense, lazy, useEffect, useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@components/ui';
import Accordion from '@components/ui/Accordion/Accordion';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import ProfileCard from '@features/tenant-profile/ProfileCard';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { getFormTemplateById } from '@/services/formTemplateService';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { useCountries } from '@/hooks/useCountries';
import { useTranslation } from '@/hooks/useTranslation';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import EditIcon from '@assets/icons/EditIcon';
import type { FormAccessLevel } from '@/types/form';
import styles from './EmployeeTemplateView.module.scss';

const FormPreview = lazy(() => import('@features/tenant-templates/FormPreview'));

// ============================================
// HELPERS
// ============================================

function useAccessLevelLabels(): Record<FormAccessLevel, string> {
  const { t } = useTranslation();
  return useMemo(() => ({
    READ_WRITE: t('K_ACCESS_READ_WRITE', 'Read & Write'),
    READ: t('K_ACCESS_READ_ONLY', 'Read Only'),
    NO_ACCESS: t('K_ACCESS_NO_ACCESS', 'No Access'),
  }), [t]);
}

// ============================================
// COMPONENT
// ============================================

interface EmployeeTemplateViewProps {
  templateId: string;
}

function EmployeeTemplateView({ templateId }: EmployeeTemplateViewProps) {
  const { t } = useTranslation();
  const ACCESS_LEVEL_LABELS = useAccessLevelLabels();
  const navigate = useNavigate();
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);

  const { data: template, isLoading } = useQuery({
    queryKey: ['formTemplate', templateId],
    queryFn: () => getFormTemplateById(templateId),
  });

  // Dynamic breadcrumb: replace static "View Template" with actual template name
  useEffect(() => {
    if (template?.name) {
      setOverride(`/w/employee-templates/view/${templateId}`, template.name);
      return () => setOverride(`/w/employee-templates/view/${templateId}`, null);
    }
  }, [template?.name, templateId, setOverride]);

  const { countries } = useCountries();

  const countryMap = useMemo(
    () => new Map(countries.map((c) => [c.id, c.name])),
    [countries],
  );

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });

  const employmentTypeMap = useMemo(
    () => new Map((employmentTypesData?.content ?? []).map((et) => [et.id, et.name])),
    [employmentTypesData],
  );

  const [activeTabId, setActiveTabId] = useState('');

  // Set initial active tab when data loads
  const tabs = template?.tabs ?? [];
  if (tabs.length > 0 && !activeTabId) {
    const firstTab = tabs[0];
    if (firstTab) {
      setActiveTabId(`tab-${0}`);
    }
  }

  const tabItems: TabItem[] = useMemo(
    () =>
      tabs.map((tab, i) => ({
        id: `tab-${i}`,
        label: tab.name || `Tab ${i + 1}`,
      })),
    [tabs],
  );

  const activeTabIndex = tabItems.findIndex((ti) => ti.id === activeTabId);
  const activeTab = activeTabIndex >= 0 ? tabs[activeTabIndex] : undefined;

  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleEdit = useCallback(() => {
    void navigate({ to: '/w/employee-templates/add', search: { id: templateId } });
  }, [navigate, templateId]);

  const profileFields = useMemo(() => {
    if (!template) return [];
    const countryName = template.countryId ? countryMap.get(template.countryId) ?? '-' : '-';
    const employmentTypeName = template.employmentTypeId
      ? employmentTypeMap.get(template.employmentTypeId) ?? '-'
      : '-';
    return [
      { label: t('K_LABEL_COUNTRY', 'Country'), value: countryName },
      { label: t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type'), value: employmentTypeName },
      { label: t('K_LABEL_TEMPLATE_DESCRIPTION', 'Description'), value: template.description || '-' },
      { label: t('K_COLUMN_VERSION', 'Version'), value: String(template.version) },
      {
        label: t('K_COLUMN_CREATED', 'Created'),
        value: new Date(template.createdAt).toLocaleDateString(),
      },
      {
        label: t('K_COLUMN_UPDATED', 'Updated'),
        value: new Date(template.updatedAt).toLocaleDateString(),
      },
    ];
  }, [template, countryMap, employmentTypeMap, t]);

  if (isLoading) {
    return <ProfileCard name="" fields={[]} loading />;
  }

  if (!template) {
    return (
      <div className={styles.errorState}>
        <p>{t('K_ERROR_TEMPLATE_NOT_FOUND', 'Template not found')}</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ProfileCard
        name={template.name || t('K_LABEL_EMPLOYEE_TEMPLATE', 'Employee Template')}
        fields={profileFields}
        avatarColor="green"
        action={
          <Button variant="outline" size="sm" leftIcon={<EditIcon />} onClick={handleEdit}>
            {t('K_BUTTON_EDIT_TEMPLATE', 'Edit Template')}
          </Button>
        }
      />

      {tabs.length > 0 ? (
        <>
          <DraggableTabs
            tabs={tabItems}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onReorder={() => {}}
            draggable={false}
          />

          <div className={styles.tabContent}>
            {activeTab ? (
              <div className={styles.tabInfo}>
                <div className={styles.tabMeta}>
                  <div className={styles.tabMetaField}>
                    <span className={styles.tabMetaLabel}>
                      {t('K_LABEL_TAB_NAME', 'Tab Name')}
                    </span>
                    <span className={styles.tabMetaValue}>{activeTab.name || '-'}</span>
                  </div>
                  <div className={styles.tabMetaField}>
                    <span className={styles.tabMetaLabel}>
                      {t('K_LABEL_TAB_DESCRIPTION', 'Description')}
                    </span>
                    <span className={styles.tabMetaValue}>{activeTab.description || '-'}</span>
                  </div>
                </div>

                <div className={styles.formList}>
                  {activeTab.forms.map((form, i) => {
                    const employeeAccess = (form.accessOverrides?.EMPLOYEE ?? form.accessLevel) as FormAccessLevel;
                    return (
                      <Accordion
                        key={form.formConfigId}
                        title={form.formName || `Form ${i + 1}`}
                        variant="filled"
                        whiteBody
                      >
                        <div className={styles.formSettings}>
                          <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>
                              {t('K_LABEL_CLIENT_ACCESS', 'Client Access')}
                            </span>
                            <span className={styles.settingValue}>
                              {ACCESS_LEVEL_LABELS[form.accessLevel] ?? form.accessLevel}
                            </span>
                          </div>
                          <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>
                              {t('K_LABEL_EMPLOYEE_ACCESS', 'Employee Access')}
                            </span>
                            <span className={styles.settingValue}>
                              {ACCESS_LEVEL_LABELS[employeeAccess] ?? employeeAccess}
                            </span>
                          </div>
                          <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>
                              {t('K_LABEL_EMPLOYEE_FORM', 'Employee Form')}
                            </span>
                            <span className={styles.settingValue}>
                              {form.attributes.isDefault
                                ? t('K_LABEL_YES', 'Yes')
                                : t('K_LABEL_NO', 'No')}
                            </span>
                          </div>
                          <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>
                              {t('K_LABEL_MANDATORY_FORM_EMPLOYEE', 'Mandatory form while creating employee')}
                            </span>
                            <span className={styles.settingValue}>
                              {form.attributes.isMandatory
                                ? t('K_LABEL_YES', 'Yes')
                                : t('K_LABEL_NO', 'No')}
                            </span>
                          </div>
                        </div>
                        <div className={styles.previewSection}>
                          <h4 className={styles.previewHeader}>
                            {t('K_LABEL_FORM_PREVIEW', 'Form Preview')}
                          </h4>
                          <Suspense fallback={<div>{t('K_LOADING', 'Loading...')}</div>}>
                            <FormPreview formConfigId={form.formConfigId} />
                          </Suspense>
                        </div>
                      </Accordion>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default EmployeeTemplateView;
